import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMigrationStatusContext } from "./MigrationStatusProvider";

// Routes that must keep working even for a cut-off employer.
//
// `/reports/preview` is a chromeless print/preview surface the reports pipeline opens directly;
// redirecting it would break report generation rather than move a person.
//
// There is deliberately no `/logout` entry: this app has no such route. Signing out is a settings
// button (`useLogout` → `/auth/logout/` → `navigate("/login")`), and `/login` renders outside the
// authenticated layout this component lives in, so a sign-out is never interrupted by a redirect.
const NEVER_REDIRECT: ReadonlyArray<string> = ["/reports/preview"];

const isExempt = (path: string): boolean =>
  NEVER_REDIRECT.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Sends a cut-off employer to Jem Hub 2.0.
 *
 * This is the last phase of the migration: read-only (`employerportal.jem2.migrated`) leaves an
 * employer able to look at the old hub, and this flag (`employerportal.jem2.redirect`) ends it.
 * Two flags rather than one so a cohort can sit read-only for as long as it takes, and so the
 * cut-off can be reversed for everyone in seconds without also restoring write access.
 *
 * Why an interstitial rather than a silent jump: the person is about to be asked to log in a
 * second time, because the old hub has to know who they are before it can know they have been cut
 * off. Landing on an unexplained login screen reads as "the system is broken". One sentence turns
 * that into "this is the move I was told about".
 *
 * `location.replace` rather than `assign` so the old hub does not sit in history — a back button
 * that returns here would redirect again, which feels like a trap.
 */
export const MigrationRedirect: React.FC = () => {
  const { isRedirected, deepLink, newHubUrl } = useMigrationStatusContext();
  const location = useLocation();
  const exempt = isExempt(location.pathname);
  const target = deepLink(location.pathname);

  useEffect(() => {
    if (!isRedirected || exempt) {
      return undefined;
    }
    // A short pause so the sentence below is actually readable — long enough to register,
    // short enough not to feel like a stall.
    const timer = window.setTimeout(() => {
      window.location.replace(target);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [isRedirected, exempt, target]);

  if (!isRedirected || exempt) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.75rem",
        padding: "2rem",
        textAlign: "center",
        background: "#ffffff",
      }}
    >
      <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>
        Taking you to Jem Hub
      </h1>
      <p style={{ margin: 0, maxWidth: "32rem", lineHeight: 1.5 }}>
        Everything you do day-to-day now lives at {newHubUrl.replace(/^https?:\/\//, "")}.
        You&rsquo;ll be asked to sign in once more — we needed to know who you are before sending
        you to the right place.
      </p>
      <a href={target} style={{ fontWeight: 600 }}>
        Go to Jem Hub now
      </a>
    </div>
  );
};
